import { styled } from '@styledComponents';
import { themeColor, themeMediaQuery, themeSpace } from '@utils/styledTheme.util';
import { animationTransition } from '@utils/animationTransition';
import { CTALink } from '@components/CTALink';
import { Column, Columns } from '@components/Columns';
import { Box } from '@components/Box';
import { Flex } from '@components/Flex';
import { TextPairing } from '@components/TextPairing';
import { Button } from '@components/Button';
import { Text } from '@components/typography';
import { JoykitIcon } from '@components/JoykitIcon';
import Carousel from 'nuka-carousel';
import { Select } from '@components/Select/Select';
import { TOP_BANNER_HEIGHT } from '@layout';
import { ButtonNoneStyled } from '@components/ButtonNoneStyled';

export const PageContentWrapper = styled.div<{ isOpen: boolean; displayHeroSection?: boolean }>`
  z-index: ${({ isOpen }) => (isOpen ? '201' : '1')};
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding-top: ${({ displayHeroSection }) => (displayHeroSection ? `calc(64px + ${TOP_BANNER_HEIGHT}px)` : themeSpace(5))};
  ${themeMediaQuery('sm')} {
    padding-top: ${({ displayHeroSection }) => (displayHeroSection ? `calc(128px + ${TOP_BANNER_HEIGHT}px)` : themeSpace(5))};
  }
  ${themeMediaQuery('xl')} {
    padding-top: ${({ displayHeroSection }) => (displayHeroSection ? `calc(192px + ${TOP_BANNER_HEIGHT}px)` : themeSpace(5))};
  }
  > * :not(.skip-margin-button) {
    margin-bottom: ${themeSpace(9)};
    ${themeMediaQuery('sm')} {
      margin-bottom: ${themeSpace(10)};
    }
  }
  position: relative;
`;

export const BackgroundGradient = styled.div`
  position: absolute;
  top: 0;
  width: 100%;
  height: 1380px;
  background: linear-gradient(to bottom, ${themeColor('neutra1')} 47.95%, rgba(249, 245, 240, 0) 70.28%);
  z-index: -1;
`;

interface ProductTypeMenuLinkProps {
  $isActive: boolean;
}

export const ProductTypeMenuLink = styled(CTALink).attrs({
  typography: 'featureCopy17',
  activeClassName: 'active',
  variant: 'neutraLink',
})<ProductTypeMenuLinkProps>`
  flex-direction: column;
  & + & {
    margin-left: ${themeSpace(10)};
  }
  ${({ $isActive, theme }) =>
    $isActive
      ? {
          color: themeColor('neutra6')({ theme }),
        }
      : {}}
  &.active {
    color: ${themeColor('neutra6')};
  }
  &:after {
    content: ' ';
    width: 48px;
    height: 4px;
    border-radius: 4px;
    margin-top: ${themeSpace(3)};
    ${({ $isActive, theme }) =>
      $isActive
        ? {
            background: themeColor('neutra6')({ theme }),
          }
        : {}}
  }
  &.active:after {
    background: ${themeColor('neutra6')};
  }
`;

export const ECardsMenusSpacingStack = styled.div`
  display: block;
  ${themeMediaQuery('below_sm')} {
    display: none;
  }
  display: flex;
  flex-direction: column;
  align-items: center;
  > *:not(:last-child) {
    margin-bottom: ${themeSpace(6)};
    ${themeMediaQuery('sm')} {
      margin-bottom: ${themeSpace(7)};
    }
  }
`;

export const ECardsMenusSpacingStackPrev = styled(ECardsMenusSpacingStack)`
  ${themeMediaQuery('below_sm')} {
    display: flex;
  }
`;

export const StyledFilterBox = styled(Box)`
  height: 55px;
`;

type CategoriesMenuItemProps = {
  isSelected?: boolean;
};

export const CategoriesMenuItem = styled(ButtonNoneStyled).attrs<CategoriesMenuItemProps>(({ isSelected }) => ({
  className: isSelected ? 'selected' : '',
}))<CategoriesMenuItemProps>`
  display: inline-block;
  cursor: pointer;
  color: ${themeColor('neutra5')};
  padding: ${themeSpace(1)} ${themeSpace(5)} 2px;
  text-align: center;
  border: 1px solid ${themeColor('neutra2')};
  border-radius: 20px;
  &:hover {
    color: ${themeColor('neutra5')};
    border: 1px solid #2c2925;
    background: ${themeColor('mono6')};
  }
  &.selected {
    color: ${themeColor('neutra5')};
    border: 2px solid #2c2925;
    background: ${themeColor('mono7')};
  }
  transition: ${animationTransition('color', 'background-color', 'border-color')};
  margin: ${themeSpace(3)} ${themeSpace(3)};
`;

const bannerHeightDesktop = 376;

export const BannerOuterBox = styled(Box)`
  width: 100%;
  align-self: stretch;
  ${themeMediaQuery('below_sm')} {
    height: auto;
    .text-pairing-body {
      min-width: 280px;
    }
    padding: 0px;
    min-height: 550px;
  }
`;

export const BannerInnerBox = styled(Box)`
  width: 100%;
  height: 100%;
  position: relative;
`;

export const BannerColumns = styled(Columns)`
  height: 100%;
  ${themeMediaQuery('below_sm')} {
    width: 311px;
    display: flex;
    padding: 0px;
    flex-direction: column-reverse;
    align-items: center;
  }
  ${themeMediaQuery('sm')} {
    flex-direction: column-reverse;
  }
  ${themeMediaQuery('md')} {
    flex-direction: row;
  }
`;

export const StyledTextPairing = styled(TextPairing)`
  ${themeMediaQuery('below_sm')} {
    width: 297px;
    min-height: 229px;
  }
`;

export const BannerColumn = styled(Column)`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  ${themeMediaQuery('below_sm')} {
    width: 311px;
    margin: auto;
  }
`;

export const CardTypeLink = styled(CTALink).attrs({ variant: 'humanaDarkLink' })`
  justify-content: center;
  ${themeMediaQuery('md')} {
    justify-content: flex-start;
  }
`;

export const BannerImageColumn = styled(Column)`
  height: 100%;
  ${themeMediaQuery('below_sm')} {
    width: 358px;
    min-height: 233px;
  }
  ${themeMediaQuery('sm')} {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
`;

export const HeroImageWrapper = styled(Box)`
  text-align: center;
  ${themeMediaQuery('below_sm')} {
    height: 233px;
  }
`;

export const HeroImage = styled.img`
  max-width: 100%;
  max-height: ${bannerHeightDesktop}px;
  width: 586px;
  height: 376px;
  ${themeMediaQuery('below_sm')} {
    width: 295px;
    height: 209px;
  }
`;

export const StyledReactPaginate = styled(Flex)<{ isDisabled?: boolean }>`
  display: ${({ isDisabled }) => (isDisabled ? 'none' : 'flex')};
`;

export const StyledReactDiv = styled(Flex)<{ isDisabled?: boolean }>`
  display: ${({ isDisabled }) => (isDisabled ? 'none' : 'flex')};
  width: 356px;
  height: 66px;
  left: 462px;
  top: 509px;
  flex-direction: column;
`;

export const StyledCTALink = styled(CTALink)`
  display: 'flex';
  justify-content: center;
  color: ${themeColor('linkDefault')};
`;

export const StyledLearnMoreFlex = styled(Flex)`
  ${themeMediaQuery('below_sm')} {
    position: relative;
    justify-content: center;
  }
  ${themeMediaQuery('sm')} {
    justify-content: center;
  }
  ${themeMediaQuery('md')} {
    justify-content: flex-start;
  }
`;

export const FilterButtonWrapper = styled.div`
  bottom: ${themeSpace(8)};
  position: fixed;
  z-index: 2;
  display: none;
  ${themeMediaQuery('below_sm')} {
    display: block;
  }
`;

export const StyledFilterButton = styled(Button)`
  border-radius: 40px;
  border: 2px solid #ebebeb;
`;

export const StyledFilterCircle = styled(Text)`
  background: #333333;
  color: white;
  font-size: 15px;
  line-height: 19px;
  font-weight: 600;
  border-radius: 16px;
  height: 22px;
  width: 22px;
  display: inline-block;
  padding: 2px;
  margin-right: ${themeSpace(4)};
`;

export const StyledFilterIcon = styled(JoykitIcon)`
  display: inline-block;
  margin-right: ${themeSpace(4)};
  vertical-align: middle;
  line-height: 19px;
  margin-top: ${themeSpace(-1)};
`;

export const StyledClearDiv = styled.div`
  color: ${themeColor('neutra5')};
  font-size: 17px;
  line-height: 27px;
  font-weight: 400;
  font-family: AkkuratLLSub-RegularSubset, sans-serif;
  letter-spacing: -0.01em;
  display: inline-block;
  cursor: pointer;
  position: absolute;
  padding-top: ${themeSpace(2)};
  right: 23%;

  &:hover {
    color: ${themeColor('linkHover')};
  }

  &:visited {
    color: ${themeColor('linkPress')};
  }

  ${themeMediaQuery('sm')} {
    right: 5%;
  }
  ${themeMediaQuery('md')} {
    right: 7%;
  }
  @media (min-width: 1400px) {
    right: 15%;
  }
  ${themeMediaQuery('lg')} {
    right: 10%;
  }
  ${themeMediaQuery('xl')} {
    right: 23%;
  }
`;

export const StyledCloseIcon = styled(JoykitIcon)`
  width: 12px;
  height: 12px;
  vertical-align: baseline;
  display: inline-block;

  &:hover {
    color: #7139bf;
  }

  &:visited {
    color: #502080;
  }
`;

export const StyledClear = styled.div`
  display: inline-block;
  padding: ${themeSpace(3)};
`;

export const StyledFilterText = styled(Text)`
  color: #333333;
  font-size: 15px;
  line-height: 19px;
  font-weight: 600;
  display: inline-block;
`;

export const FilterMenuWrapperMobile = styled.div<{ isOpen: boolean }>`
  overflow: ${({ isOpen }) => (isOpen ? 'auto' : 'hidden')};
  transform: ${({ isOpen }) => (isOpen ? 'translateY(0)' : 'translateY(-100%)')};
  transition: ${animationTransition('transform', 'max-height')};
  position: fixed;
  top: 0px;
  padding-bottom: 100px;
  width: 100%;
  height: 100vh;
  background: #ffffff;
  z-index: 200;
  ${themeMediaQuery('sm')} {
    display: none;
  }
`;

export const FilterMenuMobileDropShell = styled.div<{ isOpen: boolean }>`
  top: 0;
  width: 100%;
  text-align: center;
`;

export const MobileFilterPanel = styled.div`
  background-color: ${themeColor('mono7')};
  padding: ${themeSpace(0)} ${themeSpace(5)} ${themeSpace(5)};
  margin-top: ${themeSpace(10)};
  width: 100%;
  margin-left: auto;
  margin-right: auto;
`;

export const PanelFilterButtonContainer = styled.div`
  width: 100%;
  margin: auto;
  align-items: center;
  text-align: center;
  padding: 24px;
  position: fixed;
  bottom: 0px;
  border-top: 1px solid rgb(235, 235, 235);
  z-index: 200;
  background: white;
`;

export const StyledCommonFilterButton = styled(Button)`
  width: 100%;
`;

export const StyledMobileClose = styled.div`
  position: absolute;
  right: 22px;
  top: 22px;
`;

export const StyledMobileCloseIcon = styled(JoykitIcon)`
  width: 16px;
  height: 16px;
`;

export const StyledCarousel = styled(Carousel)`
  width: 586px !important;
  margin: auto;
  ${themeMediaQuery('below_sm')} {
    width: 338px !important;
  }
`;

//TODO: Make me more stylish
export const FilterDivider = styled.div<{ isDisplayed: boolean }>`
  display: ${({ isDisplayed }) => (isDisplayed ? 'block' : 'none')};
  margin-top: ${themeSpace(6)};
  width: 80%;
  ${themeMediaQuery('sm')} {
    margin-top: ${themeSpace(10)};
  }
  text-align: center;
  font-size: 17px;
  line-height: 27px;
`;

export const StyledBorder = styled.div`
  border-top: 1px solid #ebebeb;
`;

export const RemoveAllWrapper = styled(Flex)<{ show?: boolean }>`
  display: ${({ show }) => (show ? 'flex' : 'none')};
  width: 420px;
  flex-direction: column;
  align-items: center;
  margin-bottom: 0px;
`;

export const RemoveAllFilters = styled(CategoriesMenuItem)`
  width: 172px;
  padding: ${themeSpace(2)} ${themeSpace(4)} ${themeSpace(1)};
  margin-top: ${themeSpace(7)};
`;

export const StyledFilterCloseIcon = styled(JoykitIcon)`
  margin-top: ${themeSpace(-5)};
  width: 12px;
  height: 12px;
  margin-right: ${themeSpace(3)};
  display: inline-block;
`;

const switchButtonWidth = 172;
const switchButtonWidthMobile = 168;
const switchButtonHeight = 48;
const switchPadding = 6;

export const PrintOnlineMenu = styled.div<{ isRightSelected: boolean; isMounted: boolean }>`
  position: relative;
  width: ${switchButtonWidth * 2 + switchPadding * 2}px;
  background: rgba(220, 212, 201, 0.3);
  border-radius: 100px;
  padding: ${switchPadding}px;
  ${themeMediaQuery('below_sm')} {
    width: ${switchButtonWidthMobile * 2 + switchPadding * 2}px;
  }
  &:after {
    position: absolute;
    content: ' ';
    width: ${switchButtonWidth}px;
    height: ${switchButtonHeight}px;
    border-radius: ${switchButtonHeight / 2}px;
    background: ${themeColor('mono7')};
    box-shadow: 0px 1px 3px -1px rgba(51, 51, 51, 0.1);
    left: ${switchPadding}px;
    transform: translateX(${({ isRightSelected }) => (isRightSelected ? switchButtonWidth : 0)}px);
    visibility: ${({ isMounted }) => (isMounted ? 'visible' : 'hidden')};
    transition: ${animationTransition('transform')};
    top: ${switchPadding}px;
    z-index: 0;
    ${themeMediaQuery('below_sm')} {
      width: ${switchButtonWidthMobile}px;
      transform: translateX(${({ isRightSelected }) => (isRightSelected ? switchButtonWidthMobile : 0)}px);
    }
  }
`;

export const PrintOnlineMenuItem = styled(CTALink).attrs({ typography: 'featureCopy17', variant: 'neutraLink' })<{
  $isActive?: boolean;
}>`
  position: relative;
  width: ${switchButtonWidth}px;
  justify-content: center;
  height: ${switchButtonHeight}px;
  line-height: ${switchButtonHeight}px;
  z-index: 1;
  ${({ $isActive, theme }) => ($isActive ? { color: themeColor('neutra6')({ theme }) } : {})}
  ${themeMediaQuery('below_sm')} {
    width: ${switchButtonWidthMobile}px;
  }
`;

export const CategoriesMenu = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: ${themeSpace(-4)} ${themeSpace(-3)};
  justify-content: center;
  padding: 0px;
  ${themeMediaQuery('sm')} {
    width: 45rem;
  }
  ${themeMediaQuery('md')} {
    width: 67.5rem;
  }
  ${themeMediaQuery('lg')} {
    width: 79.5rem;
  }
  ${themeMediaQuery('xl')} {
    width: 79.5rem;
  }
`;

export const StyledText = styled(Text)``;

export const CategoriesMenuItemPrev = styled(ButtonNoneStyled).attrs<CategoriesMenuItemProps>(({ isSelected }) => ({
  className: isSelected ? 'selected' : '',
}))<CategoriesMenuItemProps>`
  color: ${themeColor('neutra5')};
  padding: 5px ${themeSpace(5)} 6px;
  text-align: center;
  border: 1px solid ${themeColor('neutra2')};
  border-radius: 20px;
  &:hover {
    cursor: pointer;
    border-color: ${themeColor('neutra3')};
    background: ${themeColor('mono6')};
    ${StyledText} {
      color: ${themeColor('neutra6')};
    }
  }
  &:active {
    border-color: ${themeColor('neutra4')};
    background: ${themeColor('neutra1')};
    ${StyledText} {
      color: ${themeColor('neutra6')};
    }
  }
  &.selected {
    border-color: ${themeColor('neutra6')};
    background: ${themeColor('mono7')};
    ${StyledText} {
      color: ${themeColor('neutra6')};
    }
  }
  transition: ${animationTransition('color', 'background-color', 'border-color')};
  margin: ${themeSpace(4)} ${themeSpace(3)};
`;

export const CategoriesMenuColumnsWrapper = styled.div`
  width: 100%;
  ${themeMediaQuery('below_sm')} {
    display: none;
  }
`;

export const CategoriesSelectColumnsWrapper = styled.div`
  width: 100%;
  ${themeMediaQuery('sm')} {
    display: none;
  }
`;

export const CategoriesSelect = styled(Select)`
  width: 100%;
  max-width: 350px;
  min-width: 320px;
  margin: auto;
  height: ${themeSpace(9)};
  border: 1px solid ${themeColor('mono3')};
  border-radius: ${themeSpace(1)};
  div {
    margin-right: ${themeSpace(4)};
  }
  select {
    text-align: left;
    padding-left: ${themeSpace(4)};
    font-size: ${themeSpace(5)};
    color: ${themeColor('mono1')};
  }
`;

export const CategoriesMenuItemWithImage = styled(ButtonNoneStyled).attrs<CategoriesMenuItemProps>(({ isSelected }) => ({
  className: isSelected ? 'selected' : '',
}))<CategoriesMenuItemProps>`
  height: 96px;
  width: 96px;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    cursor: pointer;
    border: 2px solid ${themeColor('neutra3')};
    border-radius: 50%;
  }
  &:active {
    border: 2px solid ${themeColor('neutra3')};
    border-radius: 50%;
  }
  &.selected {
    border: 2px solid ${themeColor('neutra5')};
    border-radius: 50%;
  }
`;

export const StyledSliderWrapper = styled(Box)`
  width: 100%;
  max-width: 1015px;
  ${themeMediaQuery('sm')} {
    padding-top: ${themeSpace(3)};
  }
  .react-horizontal-scrolling-menu--scroll-container {
    gap: ${themeSpace(6)};
  }
  .react-horizontal-scrolling-menu--arrow-right {
    div {
      right: -64px;
      top: 30%;
    }
  }
  .react-horizontal-scrolling-menu--arrow-left {
    div {
      left: -64px;
      top: 30%;
    }
  }
  ${themeMediaQuery('below_sm')} {
    .react-horizontal-scrolling-menu--scroll-container {
      padding: 0;
      gap: ${themeSpace(3)};
    }
    .react-horizontal-scrolling-menu--scroll-container::-webkit-scrollbar-track {
      margin: 0;
    }
    padding: 0 ${themeSpace(5)};
  }
`;
